/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { Card, Row, Col } from "antd";
import {
  FileTextOutlined,
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
} from "@ant-design/icons";
import IntlCurrencyInput from "react-intl-currency-input";

const InformacoesPagamento = ({
  statusSefaz = false,
  onSubmit,
  viewOnly,
  valorContratoInicial,
  tipoPagamentoInicial, // 0 (vista) ou 1 (prazo)
  pixInicial,
  bancoInicial,
  agenciaInicial,
  cnpjPefInicial,
  componentesIniciais, 
  responsavelInicial,
}) => {
  // --- STATES ---
  // Controle de Renderização 
  const isInitialized = useRef(false);

  const [tipoPagamento, setTipoPagamento] = useState("avista");
  const [modalidadePagamento, setModalidadePagamento] = useState(0);
  const [formaPagamento, setFormaPagamento] = useState(""); 

  // Inicia com um item vazio padrão para não quebrar a tela, mas será substituído pelo DB
  const [componentes, setComponentes] = useState([{ tipo: "", valor: "" }]);
  const [valorContrato, setValorContrato] = useState(0);

  // Dados específicos
  const [tipoChavePix, setTipoChavePix] = useState("");
  const [pix, setPix] = useState("");
  const [codBanco, setCodBanco] = useState("");
  const [codAgencia, setCodAgencia] = useState("");
  const [cnpjPef, setCnpjPef] = useState("");

  const [responsavelPagamento, setResponsavelPagamento] = useState("Embarcador");
  const [erro, setErro] = useState(""); // eslint-disable-line no-unused-vars
  const [isEditavel, setIsEditavel] = useState(false);

  // --- 1. USE EFFECT: CARREGAR DADOS DO PAI (DB) ---
  
  useEffect(() => {
    // SE JÁ INICIALIZOU, PARE AGORA! (Isso impede o loop de piscar)
    if (isInitialized.current) return;

    // Só prossegue se houver dados reais para carregar
    const temDadosValidos = 
        (componentesIniciais && componentesIniciais.length > 0) ||
        (valorContratoInicial && valorContratoInicial > 0) ||
        (pixInicial) || 
        (bancoInicial);

    if (!temDadosValidos) return; // Se ainda não tem dados, espera a próxima renderização

    // --- CARREGA OS DADOS DO BANCO NO ESTADO LOCAL ---
    
    // A. Componentes
    if (componentesIniciais && Array.isArray(componentesIniciais) && componentesIniciais.length > 0) {
      const compsFormatados = componentesIniciais.map(c => ({
        tipo: c.tpComp || c.tipo || "",
        valor: c.vComp || c.valor || 0
      }));
      setComponentes(compsFormatados);
    }

    // B. Primitivos
    if (valorContratoInicial) setValorContrato(valorContratoInicial);
    if (responsavelInicial) setResponsavelPagamento(responsavelInicial);

    // C. Lógicas de Tipo/Forma de Pagamento
    if (String(tipoPagamentoInicial) === "1") {
      setTipoPagamento("aprazo");
      setModalidadePagamento(1);
    } else {
      setTipoPagamento("avista");
      setModalidadePagamento(0);
    }

    if (pixInicial && pixInicial.length > 2) {
      setFormaPagamento("pix");
      setPix(pixInicial);
      if (pixInicial.includes("@")) setTipoChavePix("E-Mail");
      else if (pixInicial.length === 11) setTipoChavePix("CPF");
      else if (pixInicial.length === 14) setTipoChavePix("CNPJ");
      else setTipoChavePix("Celular"); 
    } else if (cnpjPefInicial && cnpjPefInicial.length > 5) {
      setFormaPagamento("pef");
      setCnpjPef(cnpjPefInicial);
    } else if (bancoInicial && bancoInicial.length > 0) {
      setFormaPagamento("deposito");
      setCodBanco(bancoInicial);
      setCodAgencia(agenciaInicial || "");
    }

    // TRAVA: Marca como inicializado para nunca mais entrar aqui
    isInitialized.current = true;

  }, [
   
    componentesIniciais, 
    valorContratoInicial, 
    responsavelInicial, 
    pixInicial, 
    bancoInicial, 
    tipoPagamentoInicial
  ]);

  // --- 2. USE EFFECT: CALCULAR TOTAL ---
  useEffect(() => {
    const total = componentes.reduce((acc, curr) => {
      let valString = String(curr.valor || "0").replace(",", ".");
      const val = parseFloat(valString) || 0;
      return acc + val;
    }, 0);

    setValorContrato(total.toFixed(2));
  }, [componentes]);

  // --- 3. USE EFFECT: ENVIAR DADOS AO PAI ---
  useEffect(() => {
    // TRAVA DE SEGURANÇA: Se não inicializou os dados do banco, NÃO manda nada pro pai.
    if (!isInitialized.current) return;

    const dadosAtuais = {
      tipoPagamento,
      modalidadePagamento,
      formaPagamento,
      
      componentes, // Array atualizado
      valorContrato,

      codBanco,
      codAgencia,
      cnpjPef,
      chavePix: pix,
      tipoChavePix,

      responsavelPagamento,
      infPagXnome: responsavelPagamento,
      infPagCnpj: "" 
    };

    if (onSubmit) {
      onSubmit(dadosAtuais);
    }
  }, [
    tipoPagamento,
    modalidadePagamento,
    formaPagamento,
    componentes,
    valorContrato,
    codBanco,
    codAgencia,
    cnpjPef,
    pix,
    tipoChavePix,
    responsavelPagamento
  ]);

  // --- HANDLERS E CONFIGS ---

  const currencyConfig = {
    locale: "pt-BR",
    formats: {
      number: {
        BRL: {
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      },
    },
  };

  const handleEditar = () => setIsEditavel((prev) => !prev);

  const handleExcluir = () => {
    setResponsavelPagamento("");
    setIsEditavel(false);
  };

  const handleAddComponente = () => {
    setComponentes([...componentes, { tipo: "", valor: "" }]);
  };

  const handleRemoveComponente = (index) => {
    const novos = [...componentes];
    novos.splice(index, 1);
    setComponentes(novos);
  };

  const handleChangeComponente = (index, field, value) => {
    const novos = [...componentes];
    novos[index][field] = value;
    setComponentes(novos);
  };

  const formatarChave = (tipo, valor) => {
    if (!valor) return "";
    let v = valor.replace(/\D/g, "");
    switch (tipo) {
      case "CPF": return v.slice(0, 11).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      case "CNPJ": return v.slice(0, 14).replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      case "Celular":
        v = v.slice(0, 11);
        if (v.length > 2) v = `(${v.slice(0, 2)}) ${v.slice(2)}`;
        if (v.length > 10) v = v.replace(/(\d{5})(\d{4})$/, "$1-$2");
        return v;
      case "E-Mail": return valor.toLowerCase().slice(0, 60);
      case "Chave Aleatória": return valor.slice(0, 36);
      default: return valor;
    }
  };

  const handlePixChange = (e) => {
    const valorBruto = e.target.value;
    if (tipoChavePix === "E-Mail" || tipoChavePix === "Chave Aleatória") {
      setPix(valorBruto);
    } else {
      setPix(formatarChave(tipoChavePix, valorBruto));
    }
  };

  return (
    <div className="tab-pane active" id="informacoesPagamento">
      
      {/* CARD 1: TIPO DE PAGAMENTO */}
      <Card
        title={<><FileTextOutlined /> Tipo de Pagamento <span style={{ color: "red" }}>*</span></>}
        style={{ marginBottom: "10px", width: "100%", padding: "25px" }}
      >
        <Row gutter={16}>
          <Col span={10}>
            <label style={{ marginTop: 10 }} className="form-label">
              Selecione o Tipo de Pagamento <span style={{ color: "red" }}>*</span>
            </label>
            <div>
              <label style={{ marginRight: 50 }}>
                <input
                  type="radio"
                  disabled={viewOnly || statusSefaz}
                  value="avista"
                  checked={tipoPagamento === "avista"}
                  onChange={() => { setTipoPagamento("avista"); setModalidadePagamento(0); }}
                /> À vista
              </label>
              <label>
                <input
                  type="radio"
                  disabled={viewOnly || statusSefaz}
                  value="aprazo"
                  checked={tipoPagamento === "aprazo"}
                  onChange={() => { setTipoPagamento("aprazo"); setModalidadePagamento(1); }}
                /> A prazo
              </label>
            </div>
          </Col>
        </Row>
      </Card>

      {/* CARD 2: COMPONENTES DE PAGAMENTO */}
      <Card
        title={<>Componentes de Pagamento <span style={{ color: "red" }}>*</span></>}
        style={{ marginBottom: "10px", width: "100%", padding: "10px" }}
      >
        {componentes.map((Comp, index) => (
          <Row gutter={16} key={index} style={{ marginBottom: "10px" }}>
            <Col span={10}>
              <div className="col-md-12">
                <label className="form-label">Tipo componente <span style={{ color: "red" }}>*</span></label>
                <select
                  disabled={viewOnly || statusSefaz}
                  className="form-control"
                  value={Comp.tipo}
                  onChange={(e) => handleChangeComponente(index, "tipo", e.target.value)}
                >
                  <option value="">Selecione...</option>
                  <option value="01">01 - Vale Pedágio</option>
                  <option value="02">02 - Impostos, taxas e contribuições</option>
                  <option value="03">03 - Despesas bancárias / outros</option>
                  <option value="04">04 - Frete</option>
                  <option value="99">99 - Outros</option>
                </select>
              </div>
            </Col>

            <Col span={4}>
              <div className="col-md-12">
                <label className="form-label">Valor componente <span style={{ color: "red" }}>*</span></label>
                <IntlCurrencyInput
                  className="form-control"
                  currency="BRL"
                  config={currencyConfig}
                  value={Comp.valor}
                  disabled={viewOnly || statusSefaz}
                  onChange={(event, value) => handleChangeComponente(index, "valor", value)}
                />
              </div>
            </Col>

            <Col span={2}>
              {index === componentes.length - 1 ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAddComponente}
                  disabled={viewOnly || statusSefaz}
                  style={{ marginTop: 30 }}
                >
                  <PlusOutlined />
                </button>
              ) : (
                <button
                  type="button"
                  disabled={viewOnly || statusSefaz}
                  onClick={() => handleRemoveComponente(index)}
                  className="btn btn-danger"
                  style={{ marginTop: 30 }}
                >
                  <DeleteOutlined />
                </button>
              )}
            </Col>
          </Row>
        ))}
      </Card>

      {/* CARD 3: VALORES E RESPONSÁVEL */}
      <Card
        title="Valores do Contrato"
        style={{ marginBottom: "10px", width: "100%", padding: "25px" }}
      >
        <Row gutter={16}>
          <Col span={10}>
            <div className="col-md-12">
              <label className="form-label">Responsável pelo Pagamento <span style={{ color: "red" }}>*</span></label>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", backgroundColor: isEditavel ? "#fff" : "#f0f0f0", padding: "5px", border: "1px solid #d9d9d9", borderRadius: "4px" }}>
                <input
                  type="text"
                  className="form-control"
                  value={responsavelPagamento}
                  onChange={(e) => setResponsavelPagamento(e.target.value)}
                  disabled={viewOnly || statusSefaz || !isEditavel}
                  style={{ flex: 1, backgroundColor: isEditavel ? "#fff" : "#f0f0f0", border: "none", boxShadow: "none" }}
                />
                <EditOutlined
                  onClick={handleEditar}
                  style={{ fontSize: "18px", color: isEditavel ? "#52c41a" : "#1890ff", cursor: "pointer" }}
                  title={isEditavel ? "Salvar edição" : "Editar campo"}
                />
                <DeleteOutlined
                  onClick={handleExcluir}
                  style={{ fontSize: "18px", color: "red", cursor: "pointer", paddingRight: "5px" }}
                  title="Excluir campo"
                />
              </div>
            </div>
          </Col>

          <Col span={6}>
            <div className="col-md-8">
              <label className="form-label">Total do Contrato <span style={{ color: "red" }}>*</span></label>
              <input
                type="text"
                className="form-control"
                disabled
                value={new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valorContrato || 0)}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* CARD 4: FORMA DE PAGAMENTO */}
      <Card
        title={<>Forma de Pagamento <span style={{ color: "red" }}>*</span></>}
        style={{ marginBottom: "10px", width: "100%", padding: "25px" }}
      >
        <Row gutter={16} style={{ marginBottom: "20px" }}>
          <Col>
            <label><input type="radio" value="deposito" disabled={viewOnly || statusSefaz} checked={formaPagamento === "deposito"} onChange={(e) => setFormaPagamento(e.target.value)} /> Depósito Bancário</label>
          </Col>
          <Col style={{marginLeft: 15}}>
            <label><input type="radio" value="pef" disabled={viewOnly || statusSefaz} checked={formaPagamento === "pef"} onChange={(e) => setFormaPagamento(e.target.value)} /> PEF - Pagamento Eletrônico Frete</label>
          </Col>
          <Col style={{marginLeft: 15}}>
            <label><input type="radio" value="pix" disabled={viewOnly || statusSefaz} checked={formaPagamento === "pix"} onChange={(e) => setFormaPagamento(e.target.value)} /> PIX</label>
          </Col>
        </Row>

        {formaPagamento === "deposito" && (
          <Row gutter={16}>
            <Col span={6}>
              <label className="form-label">Código do Banco <span style={{ color: "red" }}>*</span></label>
              <input type="text" className="form-control" disabled={viewOnly || statusSefaz} value={codBanco} onChange={(e) => setCodBanco(e.target.value)} placeholder="Ex: 001" />
            </Col>
            <Col span={6}>
              <label className="form-label">Código da Agência <span style={{ color: "red" }}>*</span></label>
              <input type="text" className="form-control" disabled={viewOnly || statusSefaz} value={codAgencia} onChange={(e) => setCodAgencia(e.target.value)} placeholder="Ex: 1234" />
            </Col>
          </Row>
        )}

        {formaPagamento === "pef" && (
          <Row gutter={16}>
            <Col span={6}>
              <label className="form-label">CNPJ da Empresa PEF <span style={{ color: "red" }}>*</span></label>
              <input type="text" className="form-control" disabled={viewOnly || statusSefaz} value={cnpjPef} onChange={(e) => setCnpjPef(formatarChave("CNPJ", e.target.value))} placeholder="CNPJ da empresa" />
            </Col>
          </Row>
        )}

        {formaPagamento === "pix" && (
          <Row gutter={16}>
            <Col span={6}>
              <label className="form-label">Tipo de Chave PIX <span style={{ color: "red" }}>*</span></label>
              <select disabled={viewOnly || statusSefaz} className="form-control" value={tipoChavePix} onChange={(e) => { setTipoChavePix(e.target.value); setPix(""); }}>
                <option value="">Selecione</option>
                <option value="CPF">CPF</option>
                <option value="CNPJ">CNPJ</option>
                <option value="Celular">Celular</option>
                <option value="E-Mail">E-Mail</option>
                <option value="Chave Aleatória">Chave Aleatória</option>
              </select>
            </Col>
            <Col span={6}>
              <label className="form-label">Insira a Chave PIX <span style={{ color: "red" }}>*</span></label>
              <input type="text" className="form-control" placeholder="Chave PIX" value={pix} disabled={viewOnly || !tipoChavePix || statusSefaz} onChange={handlePixChange} />
            </Col>
          </Row>
        )}
      </Card>
    </div>
  );
};

export default InformacoesPagamento;